import { navigateTabSkill } from "./navigateTab";
import { listMyRestaurantsSkill } from "./listMyRestaurants";
import { getAnalyticsSkill } from "./getAnalytics";
import { searchProductSkill } from "./searchProduct";
import { listCategoriesSkill } from "./listCategories";
import { getActiveOrdersSkill } from "./getActiveOrders";
import { getReservationsSkill } from "./getReservations";
import { createProductSkill } from "./createProduct";
import { setComboItemsSkill } from "./setComboItems";
import { listMenuSkill } from "./listMenu";
import { updateProductSkill } from "./updateProduct";
import { updateProductPriceSkill } from "./updateProductPrice";
import { archiveProductSkill } from "./archiveProduct";
import { createClientSkill } from "./createClient";
import { createReservationSkill } from "./createReservation";
import { closeOrderSkill } from "./closeOrder";
import {
  listTablesSkill,
  createTableSkill,
  updateTableSkill,
  deleteTableSkill,
} from "./tables";
import {
  createCategorySkill,
  updateCategorySkill,
  deleteCategorySkill,
} from "./categories";
import {
  listCashRegistersWithOpenSessionSkill,
  openCashSessionSkill,
  closeCashSessionSkill,
} from "./cashSessions";
import {
  listCashRegistersSkill,
  createCashRegisterSkill,
  updateCashRegisterSkill,
  archiveCashRegisterSkill,
  restoreCashRegisterSkill,
  toggleCashRegisterActiveSkill,
} from "./cashRegisters";
import {
  listMembersSkill,
  inviteMemberSkill,
  updateMemberRoleSkill,
  removeMemberSkill,
  listInvitationsSkill,
  cancelInvitationSkill,
} from "./members";
import {
  listAlertsSkill,
  markAlertReadSkill,
  markAllAlertsReadSkill,
} from "./alerts";
import {
  listBankTransfersSkill,
  createBankTransferSkill,
  updateBankTransferSkill,
  deleteBankTransferSkill,
} from "./bankTransfers";
import {
  listCreditAccountsSkill,
  payCreditAccountSkill,
} from "./creditAccounts";
import {
  getRestaurantInfoSkill,
  updateRestaurantSkill,
  getRestaurantPreferencesSkill,
  updateRestaurantPreferencesSkill,
  toggleRestaurantActiveSkill,
} from "./restaurant";
import {
  listClientsSkill,
  updateClientSkill,
  deleteClientSkill,
} from "./clients";
import {
  updateReservationSkill,
  deleteReservationSkill,
} from "./reservationsExtra";
import {
  closeAllForTableSkill,
  sendInvoiceSkill,
  restoreProductSkill,
} from "./ordersExtra";
import {
  listAdditionsSkill,
  createAdditionSkill,
  updateAdditionSkill,
  archiveAdditionSkill,
  restoreAdditionSkill,
  setProductAdditionsSkill,
} from "./additions";
import {
  createDineInOrderSkill,
  createDeliveryOrderSkill,
} from "./createOrder";
import { updateOrderItemSkill, removeOrderItemSkill } from "./orderItems";
import { setProductStockSkill } from "./productStock";
import {
  setProductScheduleSkill,
  setDayPromotionSkill,
  removeDayPromotionSkill,
  listDayPromotionsSkill,
} from "./productSchedule";
import { reorderProductsSkill, reorderCategoriesSkill } from "./reorder";
import { guideUserSkill } from "./guideUser";
import { setTaskListSkill, markTaskSkill } from "./taskList";
import {
  bulkCreateProductsSkill,
  bulkCreateCategoriesSkill,
  bulkDeleteCategoriesSkill,
} from "./bulk";
import type { Skill } from "./types";
import { skillToToolDef } from "./types";
import type { SkillTag } from "./tags";
import { filterSkillsByQuery } from "./tags";

/**
 * Registry whitelist + tags por dominio para lazy loading de tool defs.
 * Las skills "core" siempre se envían; las demás se filtran por keyword
 * matching en el último mensaje del usuario.
 */
const SKILL_REGISTRY: Array<{ skill: Skill; tags: SkillTag[] }> = [
  // ── Core (siempre disponibles) ──
  { skill: navigateTabSkill, tags: ["core"] },
  { skill: listMyRestaurantsSkill, tags: ["core"] },
  { skill: guideUserSkill, tags: ["core"] },
  { skill: setTaskListSkill, tags: ["core"] },
  { skill: markTaskSkill, tags: ["core"] },

  // ── Restaurante ──
  { skill: getRestaurantInfoSkill, tags: ["restaurant"] },
  { skill: updateRestaurantSkill, tags: ["restaurant"] },
  { skill: getRestaurantPreferencesSkill, tags: ["preferences"] },
  { skill: updateRestaurantPreferencesSkill, tags: ["preferences"] },
  { skill: toggleRestaurantActiveSkill, tags: ["restaurant"] },

  // ── Analítica ──
  { skill: getAnalyticsSkill, tags: ["analytics"] },

  // ── Productos ──
  { skill: listMenuSkill, tags: ["products", "categories"] },
  { skill: searchProductSkill, tags: ["products", "stock", "schedule", "additions"] },
  { skill: createProductSkill, tags: ["products", "categories"] },
  { skill: setComboItemsSkill, tags: ["products"] },
  { skill: bulkCreateProductsSkill, tags: ["products", "categories"] },
  { skill: updateProductSkill, tags: ["products", "categories"] },
  { skill: updateProductPriceSkill, tags: ["products"] },
  { skill: setProductStockSkill, tags: ["products", "stock"] },
  { skill: setProductScheduleSkill, tags: ["products", "schedule"] },
  { skill: setDayPromotionSkill, tags: ["products", "schedule"] },
  { skill: removeDayPromotionSkill, tags: ["products", "schedule"] },
  { skill: listDayPromotionsSkill, tags: ["products", "schedule"] },
  { skill: archiveProductSkill, tags: ["products"] },
  { skill: restoreProductSkill, tags: ["products"] },

  // ── Categorías ──
  { skill: listCategoriesSkill, tags: ["categories", "products"] },
  { skill: createCategorySkill, tags: ["categories"] },
  { skill: bulkCreateCategoriesSkill, tags: ["categories"] },
  { skill: updateCategorySkill, tags: ["categories"] },
  { skill: deleteCategorySkill, tags: ["categories", "products"] },
  { skill: bulkDeleteCategoriesSkill, tags: ["categories", "products"] },
  { skill: reorderCategoriesSkill, tags: ["categories"] },
  { skill: reorderProductsSkill, tags: ["products", "categories"] },

  // ── Adiciones ──
  { skill: listAdditionsSkill, tags: ["additions", "products"] },
  { skill: createAdditionSkill, tags: ["additions"] },
  { skill: updateAdditionSkill, tags: ["additions"] },
  { skill: archiveAdditionSkill, tags: ["additions"] },
  { skill: restoreAdditionSkill, tags: ["additions"] },
  { skill: setProductAdditionsSkill, tags: ["additions", "products"] },

  // ── Mesas ──
  { skill: listTablesSkill, tags: ["tables", "reservations", "orders"] },
  { skill: createTableSkill, tags: ["tables"] },
  { skill: updateTableSkill, tags: ["tables"] },
  { skill: deleteTableSkill, tags: ["tables"] },

  // ── Pedidos ──
  { skill: getActiveOrdersSkill, tags: ["orders", "kitchen", "cashier"] },
  { skill: createDineInOrderSkill, tags: ["orders", "tables"] },
  { skill: createDeliveryOrderSkill, tags: ["orders", "delivery", "clients"] },
  { skill: updateOrderItemSkill, tags: ["orders"] },
  { skill: removeOrderItemSkill, tags: ["orders"] },
  { skill: closeOrderSkill, tags: ["orders", "cashier", "cash-session"] },
  { skill: closeAllForTableSkill, tags: ["orders", "cashier", "tables"] },
  { skill: sendInvoiceSkill, tags: ["orders", "cashier"] },

  // ── Reservas ──
  { skill: getReservationsSkill, tags: ["reservations"] },
  { skill: createReservationSkill, tags: ["reservations", "clients", "tables"] },
  { skill: updateReservationSkill, tags: ["reservations"] },
  { skill: deleteReservationSkill, tags: ["reservations"] },

  // ── Clientes ──
  { skill: listClientsSkill, tags: ["clients", "credit"] },
  { skill: createClientSkill, tags: ["clients", "delivery"] },
  { skill: updateClientSkill, tags: ["clients"] },
  { skill: deleteClientSkill, tags: ["clients"] },

  // ── Caja: registradoras ──
  { skill: listCashRegistersSkill, tags: ["cash-register"] },
  { skill: listCashRegistersWithOpenSessionSkill, tags: ["cash-register", "cash-session", "cashier"] },
  { skill: createCashRegisterSkill, tags: ["cash-register"] },
  { skill: updateCashRegisterSkill, tags: ["cash-register"] },
  { skill: archiveCashRegisterSkill, tags: ["cash-register"] },
  { skill: restoreCashRegisterSkill, tags: ["cash-register"] },
  { skill: toggleCashRegisterActiveSkill, tags: ["cash-register"] },

  // ── Caja: sesiones ──
  { skill: openCashSessionSkill, tags: ["cash-session", "cashier"] },
  { skill: closeCashSessionSkill, tags: ["cash-session", "cashier"] },

  // ── Intermediarios bancarios ──
  { skill: listBankTransfersSkill, tags: ["bank-transfers"] },
  { skill: createBankTransferSkill, tags: ["bank-transfers"] },
  { skill: updateBankTransferSkill, tags: ["bank-transfers"] },
  { skill: deleteBankTransferSkill, tags: ["bank-transfers"] },

  // ── Cuentas de crédito ──
  { skill: listCreditAccountsSkill, tags: ["credit", "clients"] },
  { skill: payCreditAccountSkill, tags: ["credit", "clients", "cashier"] },

  // ── Personal ──
  { skill: listMembersSkill, tags: ["members"] },
  { skill: inviteMemberSkill, tags: ["members", "invitations"] },
  { skill: updateMemberRoleSkill, tags: ["members"] },
  { skill: removeMemberSkill, tags: ["members"] },
  { skill: listInvitationsSkill, tags: ["invitations"] },
  { skill: cancelInvitationSkill, tags: ["invitations"] },

  // ── Alertas ──
  { skill: listAlertsSkill, tags: ["alerts"] },
  { skill: markAlertReadSkill, tags: ["alerts"] },
  { skill: markAllAlertsReadSkill, tags: ["alerts"] },
];

export const ALL_SKILLS: Skill[] = SKILL_REGISTRY.map((r) => r.skill);

/** Map skillId → tags */
const SKILL_TAGS = new Map<string, SkillTag[]>(
  SKILL_REGISTRY.map((r) => [r.skill.id, r.tags]),
);

export function findSkill(id: string): Skill | undefined {
  return ALL_SKILLS.find((s) => s.id === id);
}

/**
 * Devuelve los tool defs filtrados por relevancia al mensaje del usuario.
 * Reduce dramáticamente los tokens enviados al modelo upstream.
 */
export function getToolDefsForQuery(userMessage: string) {
  const relevant = filterSkillsByQuery(ALL_SKILLS, SKILL_TAGS, userMessage);
  return relevant.map(skillToToolDef);
}

/** Devuelve TODOS los tool defs (sin filtro). Backward compat. */
export function getToolDefs() {
  return ALL_SKILLS.map(skillToToolDef);
}

export type { Skill, SkillContext, SkillResult } from "./types";
